import { motion } from 'framer-motion';
import { Car, AlertTriangle, Wrench, FileX, Building2, Globe, Shield, Clock, Award } from 'lucide-react';

const services = [
  {
    icon: Car,
    title: 'Gebrauchtwagen Ankauf',
    description: 'Alle Marken und Modelle – vom Kleinwagen bis zur Luxuslimousine. Wir bieten faire Preise unabhängig vom Alter.',
    highlight: 'Alle Marken',
  },
  {
    icon: AlertTriangle,
    title: 'Unfallwagen Ankauf',
    description: 'Auch Fahrzeuge mit Unfall- oder Karosserieschäden kaufen wir zu fairen Konditionen an.',
    highlight: 'Egal welcher Schaden',
  },
  {
    icon: Wrench,
    title: 'Motorschaden Ankauf',
    description: 'Motorschaden, Getriebeschaden oder andere technische Defekte? Kein Problem für uns.',
    highlight: 'Defekte willkommen',
  },
  {
    icon: FileX,
    title: 'Fahrzeuge ohne TÜV',
    description: 'Keine gültige Hauptuntersuchung? Wir kaufen auch Fahrzeuge ohne TÜV oder mit abgelaufener HU.',
    highlight: 'Ohne HU/AU',
  },
  {
    icon: Building2,
    title: 'Firmenflotten & Fuhrpark',
    description: 'Professioneller Ankauf kompletter Fahrzeugflotten für Unternehmen – diskret und effizient.',
    highlight: 'B2B Service',
  },
  {
    icon: Globe,
    title: 'Exportfahrzeuge',
    description: 'Auch ältere Fahrzeuge oder Modelle für den internationalen Export kaufen wir gerne an.',
    highlight: 'International',
  },
];

const premiumFeatures = [
  { icon: Shield, title: 'Sicher & Seriös', description: 'Transparente Abwicklung' },
  { icon: Clock, title: '24/7 Erreichbar', description: 'Rund um die Uhr für Sie da' },
  { icon: Award, title: 'Beste Preise', description: 'Faire Marktbewertung' },
];

export const Services = () => {
  return (
    <section id="leistungen" data-testid="services-section" className="py-24 md:py-32 bg-tiktok-black relative overflow-hidden">
      {/* Premium Background Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-tiktok-cyan/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-tiktok-pink/5 rounded-full blur-[120px]" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-[0.25em] text-tiktok-cyan mb-6 px-4 py-2 bg-tiktok-cyan/10 rounded-full border border-tiktok-cyan/20">
            Premium Ankauf Service
          </span>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Egal in welchem Zustand – wir garantieren Ihnen ein faires und transparentes Angebot für Ihr Fahrzeug.
          </p>
        </motion.div>

        {/* Premium Features Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 md:gap-8 mb-16"
        >
          {premiumFeatures.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 px-5 py-3 bg-tiktok-surface/50 rounded-2xl border border-white/5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-tiktok-cyan/20 to-tiktok-pink/20 flex items-center justify-center">
                <feature.icon className="w-5 h-5 text-tiktok-cyan" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{feature.title}</p>
                <p className="text-gray-500 text-xs">{feature.description}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Premium Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div
                data-testid={`service-card-${index}`}
                className="group relative h-full p-8 bg-gradient-to-br from-tiktok-surface to-tiktok-elevated rounded-3xl border border-white/5 hover:border-tiktok-cyan/30 transition-all duration-500 cursor-pointer overflow-hidden"
              >
                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-tiktok-cyan/0 to-tiktok-pink/0 group-hover:from-tiktok-cyan/5 group-hover:to-tiktok-pink/5 transition-all duration-500" />
                
                {/* Highlight Badge */}
                <div className="absolute top-6 right-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-tiktok-cyan/70 bg-tiktok-cyan/10 px-3 py-1 rounded-full">
                    {service.highlight}
                  </span>
                </div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-tiktok-cyan/20 via-transparent to-tiktok-pink/20 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 border border-white/10">
                    <service.icon className="w-8 h-8 text-tiktok-cyan group-hover:text-white transition-colors" />
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-tiktok-cyan transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Arrow indicator */}
                  <div className="mt-6 flex items-center gap-2 text-tiktok-cyan opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm font-semibold">Mehr erfahren</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 bg-gradient-to-r from-tiktok-surface via-tiktok-elevated to-tiktok-surface rounded-2xl border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-tiktok-cyan animate-pulse" />
              <span className="text-white font-semibold">
                Ihr Fahrzeug ist nicht dabei?
              </span>
            </div>
            <span className="text-gray-400">Kontaktieren Sie uns – wir kaufen fast alles!</span>
            <button
              onClick={() => {
                const element = document.querySelector('#autoformular');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-6 py-2 bg-gradient-to-r from-tiktok-cyan to-tiktok-pink text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
            >
              Jetzt anfragen
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
